import { useState, useRef, useEffect, useCallback } from "react";
import { FiX } from "react-icons/fi";
import styles from "./ImageCropModal.module.css";

const SIZE    = 380;  // canvas px
const CIRCLE_R = 150; // fixed circle radius

const ImageCropModal = ({ src, onConfirm, onCancel }) => {
  const canvasRef = useRef(null);
  const imgRef    = useRef(null);
  const [loaded,  setLoaded]  = useState(false);
  const [scale,   setScale]   = useState(1);
  const [offset,  setOffset]  = useState({ x: SIZE / 2, y: SIZE / 2 });
  const drag = useRef(null);

  /* ── load image ── */
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      // scale so image fills the circle on first open
      const s = Math.max((CIRCLE_R * 2) / img.width, (CIRCLE_R * 2) / img.height) * 1.05;
      setScale(s);
      setOffset({ x: SIZE / 2, y: SIZE / 2 });
      setLoaded(true);
    };
    img.src = src;
  }, [src]);

  /* ── draw ── */
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext("2d");
    const img = imgRef.current;
    const dw  = img.width  * scale;
    const dh  = img.height * scale;
    const dx  = offset.x - dw / 2;
    const dy  = offset.y - dh / 2;
    const cx  = SIZE / 2, cy = SIZE / 2;

    ctx.clearRect(0, 0, SIZE, SIZE);

    /* dim full image */
    ctx.globalAlpha = 0.38;
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.globalAlpha = 1;

    /* bright image inside circle */
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, CIRCLE_R, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.restore();

    /* circle border */
    ctx.strokeStyle = "rgba(255,255,255,0.95)";
    ctx.lineWidth   = 2.5;
    ctx.beginPath();
    ctx.arc(cx, cy, CIRCLE_R, 0, Math.PI * 2);
    ctx.stroke();
  }, [scale, offset]);

  useEffect(() => { if (loaded) draw(); }, [loaded, scale, offset, draw]);

  /* ── clamp offset so image always covers the circle ── */
  const clamp = (ox, oy, sc) => {
    const img = imgRef.current;
    if (!img) return { x: ox, y: oy };
    const dw  = img.width  * sc;
    const dh  = img.height * sc;
    const cx  = SIZE / 2, cy = SIZE / 2;
    // image edges must be outside (or on) the circle bounding box
    const minX = cx - CIRCLE_R + dw / 2;   // image center can't go too far left
    const maxX = cx + CIRCLE_R - dw / 2;
    const minY = cy - CIRCLE_R + dh / 2;
    const maxY = cy + CIRCLE_R - dh / 2;
    return {
      x: dw >= CIRCLE_R * 2 ? Math.min(Math.max(ox, maxX), minX) : cx,
      y: dh >= CIRCLE_R * 2 ? Math.min(Math.max(oy, maxY), minY) : cy,
    };
  };

  /* ── pointer helpers ── */
  const getXY = (e) =>
    e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
              : { x: e.clientX, y: e.clientY };

  const onDown = (e) => {
    e.preventDefault();
    const { x, y } = getXY(e);
    drag.current = { sx: x, sy: y, ox: offset.x, oy: offset.y };
  };
  const onMove = (e) => {
    e.preventDefault();
    if (!drag.current) return;
    const { x, y } = getXY(e);
    const newOx = drag.current.ox + (x - drag.current.sx);
    const newOy = drag.current.oy + (y - drag.current.sy);
    setOffset(clamp(newOx, newOy, scale));
  };
  const onUp = (e) => { e.preventDefault(); drag.current = null; };
  const onWheel = (e) => {
    e.preventDefault();
    const ns = Math.min(6, Math.max(0.3, scale - e.deltaY * 0.0012));
    setScale(ns);
    setOffset(o => clamp(o.x, o.y, ns));
  };

  /* ── confirm: extract exactly the circle area as 300×300 PNG ── */
  const confirm = () => {
    if (!imgRef.current) return;
    const img = imgRef.current;
    const dw  = img.width  * scale;
    const dh  = img.height * scale;
    const dx  = offset.x - dw / 2;
    const dy  = offset.y - dh / 2;
    const cx  = SIZE / 2, cy = SIZE / 2;

    /* map circle in canvas → source image coords */
    const srcX = (cx - CIRCLE_R - dx) * (img.width  / dw);
    const srcY = (cy - CIRCLE_R - dy) * (img.height / dh);
    const srcD = (CIRCLE_R * 2)       * (img.width  / dw);

    const out = document.createElement("canvas");
    out.width = 300; out.height = 300;
    const ctx = out.getContext("2d");
    /* clip to circle */
    ctx.beginPath();
    ctx.arc(150, 150, 150, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(img, srcX, srcY, srcD, srcD, 0, 0, 300, 300);
    onConfirm(out.toDataURL("image/png"));
  };

  return (
    <div className={styles.overlay}>
      {/* top bar */}
      <div className={styles.topBar}>
        <button className={styles.topBtn} onClick={onCancel}><FiX size={20} /></button>
        <span className={styles.topTitle}>Drag to adjust · scroll to zoom</span>
      </div>

      {/* canvas */}
      <div className={styles.canvasWrap}>
        <canvas
          ref={canvasRef}
          width={SIZE} height={SIZE}
          className={styles.canvas}
          onMouseDown={onDown} onMouseMove={onMove}
          onMouseUp={onUp}    onMouseLeave={onUp}
          onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
          onWheel={onWheel}
        />
        {/* zoom buttons */}
        <div className={styles.zoomBtns}>
          <button className={styles.zoomBtn}
            onClick={() => { const ns = Math.min(6, +(scale+0.15).toFixed(2)); setScale(ns); setOffset(o => clamp(o.x, o.y, ns)); }}>+</button>
          <button className={styles.zoomBtn}
            onClick={() => { const ns = Math.max(0.3, +(scale-0.15).toFixed(2)); setScale(ns); setOffset(o => clamp(o.x, o.y, ns)); }}>−</button>
        </div>
      </div>

      {/* green tick */}
      <button className={styles.confirmBtn} onClick={confirm} disabled={!loaded}>✓</button>
    </div>
  );
};

export default ImageCropModal;
